import { useState } from 'react';
import { Paper, Text, Group, Badge, Button, NumberInput, Stack, Box, Image } from '@mantine/core';
import { IconShoppingCartPlus, IconLeaf, IconStar, IconCertificate } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAddToCart } from '@/hooks/usePortalApi';
import type { Product } from '@/types';

interface ProductCardProps {
  product: Product;
}

const CATEGORY_COLORS: Record<string, string> = {
  fruit: '#e8f5e9',
  fruits: '#e8f5e9',
  vegetable: '#f1f8e9',
  vegetables: '#f1f8e9',
  dairy: '#fff8e1',
  meat: '#fce4ec',
  seafood: '#e0f7fa',
  bakery: '#fff3e0',
  organic: '#e8f5e9',
  beverages: '#e3f2fd',
  frozen: '#e8eaf6',
};

function getCategoryBg(category: string): string {
  const key = category?.toLowerCase() ?? '';
  for (const [k, v] of Object.entries(CATEGORY_COLORS)) {
    if (key.includes(k)) return v;
  }
  return '#f5f5f5';
}

export default function ProductCard({ product }: ProductCardProps) {
  const [qty, setQty] = useState<number>(product.min_order || 1);
  const [imgError, setImgError] = useState(false);
  const addToCart = useAddToCart();

  const handleAdd = () => {
    addToCart.mutate(
      { product_id: product.id, quantity: qty },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Added to cart',
            message: `${qty}x ${product.name}`,
            color: 'brand',
          });
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to add item to cart',
            color: 'red',
          });
        },
      },
    );
  };

  const hasImage = product.image_url && !imgError;

  return (
    <Paper
      withBorder
      radius="md"
      style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      {/* Product Image */}
      <Box
        style={{
          height: 140,
          background: hasImage ? undefined : getCategoryBg(product.category),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {hasImage ? (
          <Image
            src={product.image_url}
            alt={product.name}
            h={140}
            fit="cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <Stack align="center" gap={4}>
            <IconLeaf size={36} color="var(--mantine-color-produce-4)" style={{ opacity: 0.6 }} />
            <Text size="xs" c="dimmed" fw={500}>{product.category}</Text>
          </Stack>
        )}
        {product.is_staff_pick && (
          <Badge
            variant="filled"
            color="orange"
            size="sm"
            leftSection={<IconStar size={12} />}
            style={{ position: 'absolute', top: 8, left: 8 }}
          >
            Staff Pick
          </Badge>
        )}
        {!product.in_stock && (
          <Badge
            variant="filled"
            color="red"
            size="sm"
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            Out of Stock
          </Badge>
        )}
      </Box>

      {/* Product Info */}
      <Stack gap="xs" p="md" style={{ flex: 1 }}>
        <Group justify="space-between" align="start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} lineClamp={1}>
              {product.name}
            </Text>
            <Text size="xs" c="dimmed">
              {product.sku}
            </Text>
          </div>
          <Badge color="gray" variant="light" size="sm">
            {product.category}
          </Badge>
        </Group>

        {/* Filter-aware badges */}
        <Group gap={4} wrap="wrap">
          {product.is_organic && (
            <Badge size="xs" color="green" variant="light" leftSection={<IconLeaf size={10} />}>
              Organic
            </Badge>
          )}
          {product.brand && (
            <Badge size="xs" color="blue" variant="light">
              {product.brand}
            </Badge>
          )}
          {product.certifications?.map((cert) => (
            <Badge key={cert} size="xs" color="teal" variant="dot" leftSection={<IconCertificate size={10} />}>
              {cert}
            </Badge>
          ))}
        </Group>

        {product.description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {product.description}
          </Text>
        )}

        <Group justify="space-between" align="end" mt="auto">
          <div>
            <Text fw={700} size="lg" c="brand">
              ${parseFloat(product.price).toFixed(2)}
            </Text>
            <Text size="xs" c="dimmed">
              per {product.unit}
            </Text>
          </div>
          <Group gap="xs">
            <NumberInput
              value={qty}
              onChange={(v) => setQty(Number(v) || 1)}
              min={product.min_order || 1}
              step={1}
              w={70}
              size="xs"
              aria-label={`Quantity for ${product.name}`}
            />
            <Button
              size="xs"
              leftSection={<IconShoppingCartPlus size={16} />}
              onClick={handleAdd}
              loading={addToCart.isPending}
              disabled={!product.in_stock}
            >
              Add
            </Button>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}
